/**
 * /cli/device?code=XXXX-XXXX — デバイスコードでのログイン（gh auth login と同じ形・2026-10-07 console/device-login）。
 * クラウドの Claude Code などで `kumodeck login` すると、CLI がチャット / ターミナルにコードとこのリンクを出す。利用者の手順は
 * 「リンクを開いて、同じ文字なら Allow」だけ。往復・値の検査・WHY は ../cliDevice.ts、本体を呼ぶのは ../cliDeviceApi.ts。
 * 見た目と作法は CliAuthorize.tsx と同じ（AuthShell・見出し 1 文 → 結果 1 行 → ボタン 2 つ → 安全の 1 行 → ログイン中）。
 * ログインしていなければ、App.tsx のログインの木が /login へ移し、ログイン後にこの URL（?code= 込み）へ戻す。
 *
 * WHY 自動で許可しないか: 押す = 全権のセッションをその CLI に渡す。他人が自分の CLI で始めたコードのリンクを送りつけても、
 *   本人のチャットには同じ文字が無い = 本人が見比べる 1 回が守りの芯（だからコードを大きく出す）。
 * WHY 押した後にボタンを消すか: 一回限り。二度押しで 409 を見せない。
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ApiError } from '../api';
import { cleanDevice, deviceView, readUserCode, type CliDeviceResponse } from '../cliDevice';
import { approveCliDevice, cliDevicePath, denyCliDevice } from '../cliDeviceApi';
import { EmailUnverifiedNotice } from '../components/EmailUnverified';
import { Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { errorText, useT } from '../i18n';
import { useSession } from '../session';
import { AuthShell } from './AccountRecovery';

export function CliDevicePage() {
  const { t } = useT();
  const location = useLocation();
  const userCode = readUserCode(location.search);
  const res = useResource<CliDeviceResponse>(userCode ? cliDevicePath(userCode) : null);

  if (!userCode) return <End title={t('cliDevice.invalidTitle')} body={t('cliDevice.invalidBody')} />;
  if (res.loading && !res.data) {
    return (
      <AuthShell title={t('cliDevice.title')}>
        <Spinner label={t('common.loading')} />
      </AuthShell>
    );
  }
  // 404 以外のエラー（429 = 回数の制限・通信の失敗など）はその文をそのまま出す（errorText = 他の画面と同じ言い方）
  if (res.error && res.error.status !== 404 && res.error.status !== 410) return <End title={t('cliDevice.title')} body={errorText(res.error)} />;
  const view = deviceView({ data: res.data, errorStatus: res.error?.status ?? null });
  if (view === 'notFound') return <End title={t('cliDevice.notFoundTitle')} body={t('cliDevice.notFoundBody')} />;
  if (view === 'expired') return <End title={t('cliDevice.expiredTitle')} body={t('cliDevice.expiredBody')} />;
  if (view === 'used') return <End title={t('cliDevice.usedTitle')} body={t('cliDevice.usedBody')} />;
  return <Ask req={res.data!} reload={res.reload} />;
}

function End({ title, body }: { title: string; body: string }) {
  return (
    <AuthShell title={title}>
      <p role="status">{body}</p>
    </AuthShell>
  );
}

function Ask({ req, reload }: { req: CliDeviceResponse; reload: () => void }) {
  const { t, tr } = useT();
  const { developer } = useSession();
  const [state, setState] = useState<'idle' | 'busy' | 'allowed' | 'cancelled'>('idle');
  const [error, setError] = useState<ApiError | null>(null);
  const unverified = developer?.emailVerified === false || error?.code === 'email_unverified';
  const device = cleanDevice(req.device);

  const answer = async (allow: boolean) => {
    setState('busy');
    setError(null);
    try {
      await (allow ? approveCliDevice(req.userCode) : denyCliDevice(req.userCode));
      setState(allow ? 'allowed' : 'cancelled');
    } catch (err) {
      const e = err instanceof ApiError ? err : new ApiError(0, 'client_error', String(err));
      // 409 = もう pending ではない（期限切れ・ほかのタブで答えた）。読み直して「期限切れ / 使用済み」の画面にする
      if (e.status === 409) return reload();
      // Cancel が失敗しても「ログインしていない」は変わらない（許可は出ていない・コードは 10 分で切れる）= やめた画面へ。
      // WHY: メール未確認だと deny も 403 になりうる（cli-grants と同じ条件）。そこで止めると「やめたいのにやめられない」に見える
      if (!allow) return setState('cancelled');
      setState('idle');
      setError(e);
    }
  };

  if (state === 'allowed') return <End title={t('cliDevice.allowedTitle')} body={t('cliDevice.allowedBody')} />;
  if (state === 'cancelled') return <End title={t('cliDevice.cancelledTitle')} body={t('cliDevice.cancelledBody')} />;

  // 並び（CliAuthorize.tsx と同じ）: 見出し → 見比べる 1 文 → コード（大きく）→ 端末の名前 → ボタン 2 つ → 安全の 1 行 → ログイン中
  return (
    <AuthShell title={t('cliDevice.title')}>
      <p className="confirm-plain-result">{t('cliDevice.lead')}</p>
      {/* 見比べる文字。読み上げでも 1 文字ずつ読まれるよう aria-label に空白区切りを入れる */}
      <p className="cli-device-code" aria-label={req.userCode.split('').join(' ')}>
        {req.userCode}
      </p>
      {/* 利用者（CLI）が決めた値は文言に混ぜず、テキストとして別の要素に出す */}
      <p className="small muted">{tr('cliDevice.device', { device: <strong>{device ?? t('cliAuth.deviceUnknown')}</strong> })}</p>
      {unverified ? (
        <>
          <EmailUnverifiedNotice />
          <p className="small muted">{t('cliDevice.unverifiedThen')}</p>
        </>
      ) : null}
      {error && !unverified && (
        <p className="alert alert-error" role="alert">
          {errorText(error)}
        </p>
      )}
      <div className="confirm-plain-actions">
        {!unverified && (
          <button type="button" className="btn btn-primary confirm-plain-btn" onClick={() => void answer(true)} disabled={state === 'busy'}>
            {state === 'busy' ? t('common.working') : t('cliDevice.allow')}
          </button>
        )}
        <button type="button" className="btn confirm-plain-btn" onClick={() => void answer(false)} disabled={state === 'busy'}>
          {t('cliAuth.cancel')}
        </button>
      </div>
      {/* 「自分で頼んでいないなら Cancel」= CliAuthorize と同じ 1 行（指示書） */}
      <p className="confirm-plain-safety" role="note">
        <strong>{t('cliAuth.safety')}</strong>
      </p>
      <p className="small muted">{tr('cliAuth.signedInAs', { email: <strong>{developer?.email ?? ''}</strong> })}</p>
    </AuthShell>
  );
}
